import {
  AutoComplete,
  Button,
  DatePicker,
  Form,
  Input,
  Select,
  Spin,
} from "antd";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { authenticateToken } from "../../utils/auth";
import { getPage, getParamValue } from "../../utils/index";
import Loading from "../common/Loader";
import Empty from "../common/Empty";
import moment from "moment";
import TopHeading from "./TopHeading";
import { debounce } from "lodash";
import { useLocation } from "react-router";
import PaginationTwo from "../common/PaginationTwo";
const { RangePicker } = DatePicker;
const { Option } = Select;

const DeliveryReportList = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(undefined);
  const [status, setStatus] = useState<any>();
  const [limit, setLimit] = useState(16);
  const loc = useLocation();
  const page = getParamValue(loc.search, "page");
  const [paymentMethod, setPaymentMethod] = useState<any>();
  const [type, setType] = useState("JC_MART");
  const [mobileNumber, setMobileNumber] = useState("");

  const [range, setRange] = useState({
    from: null,
    to: null,
  });

  const [ordersData, setOrdersData] = useState({
    loading: false,
    data: null,
  });

  const [driversOptions, setDriversOptions] = useState({
    loading: false,
    list: null,
  });

  const [methodOptions, setMethodOptions] = useState({
    loading: false,
    list: null,
  });

  const [downloadData, setDownloadData] = useState({
    loading: false,
    data: null,
  });

  const getDeliveryReport = useCallback(async () => {
    const from = moment(range.from)?.toISOString();
    const to = moment(range.to)?.toISOString();

    setOrdersData({ loading: true, data: null });
    const encodedUri = `${process.env.REACT_APP_ORDER_API}`;
    axios
      .get(
        `${encodedUri}/finance/delivery-hero?page=${page || 0}&limit=${
          limit || 16
        }` +
          (mobileNumber
            ? `&mobileNumber=%2B88${mobileNumber?.replace("+88", "")}`
            : ``) +
          (paymentMethod ? `&paymentMethod=${paymentMethod}` : ``) +
          (from ? `&startFrom=${from}` : ``) +
          (to ? `&endAt=${to}` : ``),
        {
          headers: {
            Authorization: `Bearer ${authenticateToken()}`,
          },
        }
      )
      .then((res) => {
        setOrdersData({ loading: false, data: res.data });
      })
      .catch((err) => {
        setOrdersData({ loading: false, data: [] });
        console.error("Data: Error", err);
      });
  }, [range.from, range.to, page, limit, mobileNumber, paymentMethod]);

  const getDeliveryReportDownload = useCallback(async () => {
    const from = moment(range.from)?.toISOString();
    const to = moment(range.to)?.toISOString();

    setDownloadData({ loading: true, data: null });
    const encodedUri = `${process.env.REACT_APP_ORDER_API}`;
    fetch(
      `${encodedUri}/finance/delivery-hero/download?page=0` +
        (from ? `&startFrom=${from}` : ``) +
        (to ? `&endAt=${to}` : ``) +
        (mobileNumber
          ? `&mobileNumber=%2B88${mobileNumber?.replace("+88", "")}`
          : ``) +
        (paymentMethod ? `&paymentMethod=${paymentMethod}` : ``),
      {
        headers: {
          Authorization: `Bearer ${authenticateToken()}`,
        },
      }
    )
      .then((response) => {
        return response.blob();
      })
      .then((res) => {
        setDownloadData({ loading: false, data: res });
        const url = window.URL.createObjectURL(new Blob([res]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `Delivery_Report-${moment(range.from).format("YYYY_MM_DD")}-${moment(
            range.to
          ).format("YYYY_MM_DD")}.xlsx`
        );
        link.click();
        // saveAs(res, `revenue-report-${Math.random().toString(36)}.xlsx`);
      })
      .catch((err) => {
        console.error("Data: Error", err);
      });
  }, [mobileNumber, paymentMethod, range.from, range.to]);

  //   ************************************
  //   ************************************

  const getDriversOptions = useCallback(
    async (mobileNumber) => {
      setDriversOptions({ loading: true, list: null });
      const encodedUri = `${process.env.REACT_APP_RIDER_API}`;
      axios
        .get(
          `${encodedUri}/admin/driver?` +
            (mobileNumber
              ? `&mobileNumberOrName=${mobileNumber?.replace("+88", "")}`
              : ``) +
            (status ? `&status=${status}` : ``) +
            `&page=${0}` +
            `&limit=${20}`,
          {
            headers: {
              Authorization: `Bearer ${authenticateToken()}`,
            },
          }
        )
        .then((res) => {
          setDriversOptions({
            loading: false,
            list: res.data?.drivers?.map((driver: any) => ({
              label: `${driver.mobileNumber.replace("+88", "")} ${driver.name}`,
              value: driver.mobileNumber.replace("+88", ""),
            })),
          });
        })
        .catch((err) => {
          setDriversOptions({ loading: false, list: [] });
          console.error("Drivers: Error", err);
        });
    },
    [status]
  );

  const getMethodOptions = useCallback(async () => {
    setMethodOptions({ loading: true, list: null });
    const encodedUri = `${process.env.REACT_APP_PAY_API}`;
    axios
      .get(
        `${encodedUri}/payment-method?page=${page || 0}&limit=${limit || 16}` +
          (type ? `&type=${type}` : `JC_MART`) +
          (status ? `&status=${status}` : ``),
        {
          headers: {
            Authorization: `Bearer ${authenticateToken()}`,
          },
        }
      )
      .then((res) => {
        setMethodOptions({
          loading: false,
          list: res.data?.methods?.map((method) => ({
            label: method.title,
            value: method.name,
          })),
        });
      })
      .catch((err) => {
        setMethodOptions({ loading: false, list: [] });
        console.error("Method: Error", err);
      });
  }, [limit, status, page, type]);

  const fetchRef = useRef(null);
  const handleSearch = React.useMemo(() => {
    const loadOptions = (value: string, field: string) => {
      fetchRef.current += 1;
      const fetchId = fetchRef.current;

      if (fetchId !== fetchRef.current) {
        return;
      }
      if (value) {
        if (field === "driver") getDriversOptions(value);
      }
    };

    return debounce(loadOptions, 800);
  }, [getDriversOptions]);

  //   ************************************
  //   ************************************

  useEffect(() => {
    getDeliveryReport();
  }, [getDeliveryReport]);

  useEffect(() => {
    getMethodOptions();
  }, [getMethodOptions]);

  const reseAllFieldData = () => {
    setType("");
    setMobileNumber("");
    form.resetFields();
    setRange({
      from: null,
      to: null,
    });
  };

  const totalRevenue = useMemo(() => {
    return (
      ordersData?.data?.reports?.reduce((obj, data) => {
        const newObj = {};

        const onlyMethods = { ...obj, ...newObj };
        let allMethodsTotal = data?.total;
        Object.keys(onlyMethods)?.forEach((item) => {
          allMethodsTotal += onlyMethods?.[item];
        });

        return {
          ...onlyMethods,
          total: allMethodsTotal,
        };
      }, {}) || {}
    );
  }, [ordersData]);

  return (
    <React.Fragment>
      <TopHeading
        {...ordersData?.data?.totalElements}
        refetch={getDeliveryReport}
        title={"Delivery Report"}
        download={() => getDeliveryReportDownload()}
        dissable={!ordersData?.data?.reports?.length ? true : false}
        loading={downloadData?.loading}
      />
      <section className="search_area">
        <Form form={form} className="search_form">
          <Form.Item name="range">
            <RangePicker
              defaultValue={
                range.from != null && range.to != null
                  ? [moment(range.from), moment(range.to)]
                  : null
              }
              onChange={(e, f) => {
                console.log("momnet", moment.utc(f[0])?.toISOString());
                setRange({ from: f?.[0], to: f?.[1] });
              }}
              showTime={{
                hideDisabledOptions: true,
                defaultValue: [
                  moment("00:00:00", "HH:mm:ss"),
                  moment("23:59:59", "HH:mm:ss"),
                ],
              }}
              inputReadOnly
              allowClear
            />
          </Form.Item>

          <Form.Item name="method_search" initialValue={paymentMethod}>
            <Select
              allowClear
              showSearch
              placeholder="Payment Method"
              optionFilterProp="children"
              onChange={(val) => setPaymentMethod(val)}
              onSearch={(e) => handleSearch(e, "method")}
              filterOption={(input, option) =>
                option.children
                  .toString()
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
            >
              {methodOptions?.list?.length ? (
                <Option value={""}>All Methods</Option>
              ) : undefined}
              {methodOptions?.list?.map((method, i) => (
                <Option value={method?.value} key={i}>
                  {method?.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="search">
            <AutoComplete
              style={{ width: 300 }}
              onSearch={(e) => handleSearch(e, "driver")}
              onSelect={(val) => setMobileNumber(val.toString())}
              options={driversOptions?.list}
              defaultActiveFirstOption={false}
              notFoundContent={
                driversOptions?.loading ? <Spin size="small" /> : null
              }
            >
              <Input.Search
                size="large"
                placeholder="Search by Phone (01...)"
                onSearch={(val) => setMobileNumber(val)}
                enterButton
                loading={driversOptions.loading}
                pattern={`[0-9]`}
                maxLength={11}
              />
            </AutoComplete>
          </Form.Item>
        </Form>
        <div className="search_btn">
          <Button type="primary" size="large" danger onClick={reseAllFieldData}>
            Reset
          </Button>
        </div>
      </section>

      <div className="product-list-item-area white-bg section-padding-bottom">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="product-list-items-inner-content">
                <div className="single-product-wrapper">
                  <div className="single-product-heading">
                    <span>Order Id</span>
                  </div>
                  <div className="single-product-heading">
                    <span>Date</span>
                  </div>
                  <div className="single-product-heading">
                    <span>Delivery Hero</span>
                  </div>
                  <div className="single-product-heading">
                    <span>DH Contact </span>
                  </div>
                  <div className="single-product-heading">
                    <span>Method</span>
                  </div>
                  <div className="single-product-heading">
                    <span>Total</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            {ordersData?.loading ? (
              <Loading />
            ) : (
              <div
                className="col-lg-12 product-table"
                style={{ maxHeight: `calc(100vh - 380px)` }}
              >
                <div className="product-table-area">
                  {ordersData?.data?.reports?.length ? (
                    ordersData?.data?.reports?.map(
                      (ordersData: any, index: any) => (
                        <React.Fragment key={index}>
                          <div className="single-product-wrapper">
                            <div className="single-product">
                              <span className="name">
                                {ordersData?.orderId}
                              </span>
                            </div>
                            <div className="single-product">
                              <span className="name">
                                {moment(ordersData?.createdAt).format("lll")}
                              </span>
                            </div>

                            <div className="single-product">
                              <span className="name">
                                {ordersData?.deliveryMan?.name}
                              </span>
                            </div>

                            <div className="single-product">
                              <span className="name">
                                {ordersData?.deliveryMan?.mobileNumber?.split(
                                  "+88"
                                )}
                              </span>
                            </div>

                            <div className="single-product">
                              <span className="name">
                                {ordersData?.paymentMethod}
                              </span>
                            </div>

                            <div className="single-product">
                              <span
                                className={`name ${
                                  ordersData?.revenue <= 0
                                    ? `text-danger font-weight-bold`
                                    : ``
                                }`}
                              >
                                {parseInt(ordersData?.total)}
                              </span>
                            </div>
                          </div>
                        </React.Fragment>
                      )
                    )
                  ) : (
                    <Empty data="Data" />
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="row">
            {ordersData?.loading ? (
              <Loading />
            ) : (
              <div className="col-lg-12">
                <div className="product-table-area ">
                  <div className="single-product-wrapper">
                    <div className="single-product">
                      <b className="name"></b>
                    </div>
                    <div className="single-product">
                      <b className="name"></b>
                    </div>
                    <div className="single-product">
                      <b className="name"></b>
                    </div>
                    <div className="single-product">
                      <b className="name"></b>
                    </div>

                    <div className="single-product">
                      <b className="name font-weight-bold">Total</b>
                    </div>
                    <div className="single-product">
                      <span
                        className={`name font-weight-bold ${
                          totalRevenue?.total <= 0 ? `text-danger` : ``
                        }`}
                      >
                        &#2547;{parseInt(totalRevenue?.total) || 0}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <PaginationTwo
            {...ordersData?.data}
            limit={limit}
            page={getPage(loc.search)}
          />
        </div>
      </div>
    </React.Fragment>
  );
};

export default DeliveryReportList;
